import { useForm } from "react-hook-form";
import { useCallback, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormGroup } from "@mui/material";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { TableOptions } from "../../../../../../common/components/table_options";
import { defaultReportNovosNegociosForm, ReportNovosNegociosForm, ReportNovosNegociosSchema } from "../../../schemas/reports/novosNegociosReport";

/**
 * ReportMapaConcretagemModal
 */

type Props = {
  open: boolean;
  loading: boolean;
  saving: boolean;
  ddlCentroCusto: CentroCustoDropDown[];
  onClose: (openState: boolean) => void;
  onSubmit: (form: ReportNovosNegociosForm) => void;
};

export const ReportNovosNegociosModal = ({
  open,
  loading,
  saving,
  ddlCentroCusto,

  onClose,
  onSubmit,
}: Props) => {

  const hookForm = useForm<ReportNovosNegociosForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: defaultReportNovosNegociosForm,
    resolver: zodResolver(ReportNovosNegociosSchema),
  });

  const [c] = hookForm.watch(['colunas']);

  const status = useMemo(() => {
    return [
      { id: 4, title: 'Todas' },
      { id: 0, title: 'Em análise' },
      { id: 2, title: 'Descartar' },
      { id: 8, title: 'Processo concluído com sucesso' },
      { id: 1, title: 'Participar da licitação - Todas' },
      { id: 3, title: 'Participar da licitação - Ganha' },
      { id: 5, title: 'Participar da licitação - Perdida' },
      { id: 6, title: 'Participar da licitação - Inabilitada' },
      { id: 7, title: 'Participar da licitação - Outros' }
    ];
  }, []);


  const selectAll = useMemo(() => {
    if (!c) return false;

    return (
      c.centroCusto &&
      c.nroNegocio &&
      c.data &&
      c.cliente &&
      c.cidade &&
      c.telefone &&
      c.nroEdital &&
      c.informacoes
    );
  }, [c]);

  const handleToggleSelectAll = useCallback((checked: boolean) => {
    hookForm.setValue('colunas', {
      nroNegocio: checked,
      data: checked,
      cliente: checked,
      cidade: checked,
      telefone: checked,
      nroEdital: checked,
      centroCusto: checked,
      informacoes: checked,
    });
  }, [
    hookForm,
  ]);

  const handleToggleOptionChange = useCallback((field: keyof ReportNovosNegociosForm['colunas'], checked: boolean) => {
    const colunas = hookForm.getValues('colunas');
    colunas[field] = checked;
    hookForm.setValue('colunas', { ...colunas });
  }, [
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title={'Relatório Novos Negócios'}
      width='70rem'
      confirmButtonLabel='Gerar relatório'
      confirmSavingButtonLabel='Gerando relatório...'
      savingLabel="Gerando"
      // leftButtonSlot={<PrimaryButton onClick={() => {}}>Salvar Colunas</PrimaryButton>}
      onClose={() => onClose(false)}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <div className="w-full grid grid-cols-2 grid-rows-1 gap-2 p-4">
        <div className="col-span-6">
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <div className="w-full flex gap-1">
                <DefaultFormInput
                  name='titulo'
                  label='Título'
                  reactHookForm={hookForm} />

                {/* <DefaultFormInput
                  name=''
                  label='Título/Pedido'
                  reactHookForm={hookForm} /> */}

              </div>


              <DefaultFormMultiAutocomplete
                name='centroCusto'
                label='Centro de Custo'
                options={ddlCentroCusto}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />


              <DefaultFormMultiAutocomplete
                name='status'
                label='Status'
                options={status}
                getOptionKey={x => x.id}
                getOptionLabel={x => x.title}
                reactHookForm={hookForm} />

              <div>
                <div className="w-full flex gap-1">
                  <FormDatePickerInput
                    name='dataDe'
                    label='Data De'
                    reactHookForm={hookForm} />

                  <FormDatePickerInput
                    name='dataAte'
                    label='Data Ate'
                    reactHookForm={hookForm} />
                </div>
              </div>
            </>
          </FormContainer>
        </div>

        <div className="col-start-7 col-span-4 px-8 py-8">
          <FormGroup>
            <TableOptions label="EXIBIR TODAS AS COLUNAS" checked={selectAll} onChange={c => handleToggleSelectAll(c)} />
            <div className="flex flex-col pl-4">
              <TableOptions checked={c?.nroNegocio} label="Nº NEGÓCIO" onChange={v => handleToggleOptionChange('nroNegocio', v)} />
              <TableOptions checked={c?.data} label="DATA" onChange={v => handleToggleOptionChange('data', v)} />
              <TableOptions checked={c?.cliente} label="CLIENTE" onChange={v => handleToggleOptionChange('cliente', v)} />
              <TableOptions checked={c?.cidade} label=" CIDADE" onChange={v => handleToggleOptionChange('cidade', v)} />
              <TableOptions checked={c?.telefone} label="TELEFONE" onChange={v => handleToggleOptionChange('telefone', v)} />
              <TableOptions checked={c?.nroEdital} label="Nº EDITAL" onChange={v => handleToggleOptionChange('nroEdital', v)} />
              <TableOptions checked={c?.centroCusto} label="CENTRO DE CUSTO" onChange={v => handleToggleOptionChange('centroCusto', v)} />
              <TableOptions checked={c?.informacoes} label="INFORMAÇÕES" onChange={v => handleToggleOptionChange('informacoes', v)} />
            </div>
          </FormGroup>
        </div>
      </div>
    </BaseFormControlledModal>
  );
}